import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StackScreenProps } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/types';
import { Star, ScanLine, Sparkles, Pencil, BookOpen, School, ArrowLeft } from 'lucide-react-native';
import { CopyrightFooter } from '../components/CopyrightFooter';
import { DemoService } from '../services/DemoService';
import { useDeviceStore } from '../store/useDeviceStore';

type Props = StackScreenProps<RootStackParamList, 'Home'>;

export function HomeScreen({ navigation, route }: Props) {
    const [demoLoading, setDemoLoading] = useState(false);
    const [demoError, setDemoError] = useState<string | null>(null);
    const [isDemoMode, setIsDemoMode] = useState(Boolean(route.params?.isDemoMode));

    const { isRegistered, schoolName, initializeDevice } = useDeviceStore();

    useEffect(() => {
        initializeDevice().catch(err => console.warn('[HomeScreen] Device init warning:', err));
    }, []);

    useEffect(() => {
        if (route.params?.isDemoMode !== undefined) {
            setIsDemoMode(route.params.isDemoMode);
        }
    }, [route.params?.isDemoMode]);

    const handleLaunchDemoLesson = async () => {
        if (demoLoading) return;
        setDemoError(null);
        setDemoLoading(true);
        try {
            const info = await DemoService.fetchDemoClassInfo();
            navigation.navigate('Discovering', { classId: info.classId, serverUrl: info.serverUrl, teacherCode: info.teacherCode });
        } catch {
            setDemoError("Let's try again!");
        } finally {
            setDemoLoading(false);
        }
    };

    const showHub = isRegistered || isDemoMode;

    return (
        <SafeAreaView style={styles.container}>
            {/* Large background blobs */}
            <View style={[styles.blob, styles.blobTopRight]} />
            <View style={[styles.blob, styles.blobMidLeft]} />
            <View style={[styles.blob, styles.blobBottomRight]} />
            <View style={[styles.blob, styles.blobTopLeft]} />

            {/* Floating sparkle decorations */}
            <View style={[styles.sparkle, { top: 80, left: 40 }]}>
                <Star size={16} color="#FAAD14" fill="#FAAD14" />
            </View>
            <View style={[styles.sparkle, { top: 140, right: 60 }]}>
                <Sparkles size={20} color="#52C41A" />
            </View>
            <View style={[styles.sparkle, { top: 320, right: 30 }]}>
                <Star size={14} color="#1890FF" fill="#1890FF" />
            </View>
            <View style={[styles.sparkle, { bottom: 200, left: 50 }]}>
                <Sparkles size={18} color="#FA8C16" />
            </View>
            <View style={[styles.sparkle, { bottom: 120, right: 80 }]}>
                <Star size={12} color="#EB2F96" fill="#EB2F96" />
            </View>

            {/* Floating dots */}
            <View style={[styles.dot, styles.dot1]} />
            <View style={[styles.dot, styles.dot2]} />
            <View style={[styles.dot, styles.dot3]} />
            <View style={[styles.dot, styles.dot4]} />

            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                bounces={false}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
            >
                <View style={styles.content}>
                    {/* Icon cluster */}
                    <View style={styles.iconCluster}>
                        <View style={[styles.iconOrb, styles.orbBackLeft]}>
                            <Pencil size={28} color="#FFFFFF" strokeWidth={2.5} />
                        </View>
                        <View style={[styles.iconOrb, styles.orbBackRight]}>
                            <BookOpen size={28} color="#FFFFFF" strokeWidth={2.5} />
                        </View>
                        <View style={styles.mainOrb}>
                            <Star size={64} color="#FAAD14" fill="#FAAD14" />
                        </View>
                    </View>

                    {showHub ? (
                        <>
                            {isRegistered ? (
                                <View style={styles.schoolBadge}>
                                    <School size={18} color="#0D9488" />
                                    <Text style={styles.schoolBadgeText} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                                        {schoolName || 'School Tablet'}
                                    </Text>
                                </View>
                            ) : (
                                <View style={styles.demoBadgeRow}>
                                    <TouchableOpacity
                                        style={styles.backButton}
                                        onPress={() => {
                                            setDemoError(null);
                                            setIsDemoMode(false);
                                        }}
                                        accessibilityLabel="Back to Welcome screen"
                                        accessibilityRole="button"
                                        accessibilityHint="Returns to the welcome screen"
                                    >
                                        <ArrowLeft size={18} color="#475569" strokeWidth={2.5} />
                                        <Text style={styles.backButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.3}>Back</Text>
                                    </TouchableOpacity>
                                    <View style={styles.demoHubBadge}>
                                        <Sparkles size={16} color="#722ED1" />
                                        <Text style={styles.demoHubBadgeText} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                                            Demo School
                                        </Text>
                                    </View>
                                </View>
                            )}

                            <Text style={styles.title} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Ready to Learn?</Text>
                            <Text style={styles.subtitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                Practice on your own or join your teacher!
                            </Text>

                            <View style={styles.modeButtonsContainer}>
                                {/* Free Learning Mode */}
                                <TouchableOpacity
                                    style={styles.freestyleModeCard}
                                    onPress={() => navigation.navigate('FreestyleCurriculum')}
                                    activeOpacity={0.8}
                                    accessibilityLabel="Free Learning Mode"
                                    accessibilityRole="button"
                                    accessibilityHint="Practice maths and curriculum subjects on your own without a QR code"
                                >
                                    <View style={styles.modeIconCircleGold}>
                                        <Sparkles size={28} color="#D97706" strokeWidth={2.5} />
                                    </View>
                                    <View style={styles.modeTextContainer}>
                                        <Text style={styles.modeTitle} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                                            Free Learning
                                        </Text>
                                        <Text style={styles.modeDescription} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                                            Practice maths & subjects on your own
                                        </Text>
                                    </View>
                                </TouchableOpacity>

                                {/* Classroom Mode */}
                                {isRegistered ? (
                                    <TouchableOpacity
                                        style={styles.classroomModeCard}
                                        onPress={() => navigation.navigate('Scan')}
                                        activeOpacity={0.8}
                                        accessibilityLabel="Classroom Mode - Scan to Join"
                                        accessibilityRole="button"
                                        accessibilityHint="Opens the camera to scan your teacher's QR code"
                                    >
                                        <View style={styles.modeIconCircleGreen}>
                                            <ScanLine size={28} color="#15803D" strokeWidth={2.5} />
                                        </View>
                                        <View style={styles.modeTextContainer}>
                                            <Text style={styles.modeTitle} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                                                Classroom Mode
                                            </Text>
                                            <Text style={styles.modeDescription} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                                                Scan teacher code to join live lesson
                                            </Text>
                                        </View>
                                    </TouchableOpacity>
                                ) : (
                                    <TouchableOpacity
                                        style={[styles.classroomModeCard, demoLoading && styles.classroomModeCardBusy]}
                                        onPress={handleLaunchDemoLesson}
                                        disabled={demoLoading}
                                        activeOpacity={0.8}
                                        accessibilityState={demoLoading ? { busy: true } : undefined}
                                        accessibilityLabel="Classroom Mode - Try Demo Lesson"
                                        accessibilityRole="button"
                                        accessibilityHint="Launches a simulated live demo lesson"
                                    >
                                        <View style={styles.modeIconCircleGreen}>
                                            {demoLoading ? (
                                                <ActivityIndicator size="small" color="#15803D" />
                                            ) : (
                                                <ScanLine size={28} color="#15803D" strokeWidth={2.5} />
                                            )}
                                        </View>
                                        <View style={styles.modeTextContainer}>
                                            <Text style={styles.modeTitle} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                                                Classroom Mode
                                            </Text>
                                            <Text style={styles.modeDescription} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                                                {demoLoading ? 'Connecting to demo class...' : 'Try a live demo lesson'}
                                            </Text>
                                        </View>
                                    </TouchableOpacity>
                                )}

                                {demoError ? (
                                    <Text style={styles.demoErrorText} accessibilityLiveRegion="polite" allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                        {demoError}
                                    </Text>
                                ) : null}
                            </View>
                        </>
                    ) : (
                        <>
                            <Text style={styles.title} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Ready to Learn?</Text>
                            <Text style={styles.subtitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                Scan your teacher's code and jump in!
                            </Text>

                            <TouchableOpacity
                                style={styles.scanButton}
                                onPress={() => navigation.navigate('Scan')}
                                activeOpacity={0.75}
                                accessibilityLabel="Scan to Join"
                                accessibilityRole="button"
                                accessibilityHint="Opens the camera to scan your teacher's QR code"
                            >
                                <ScanLine size={32} color="#FFFFFF" strokeWidth={3} />
                                <Text style={styles.scanButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Scan to Join</Text>
                            </TouchableOpacity>

                            <TouchableOpacity
                                style={styles.demoButton}
                                onPress={() => {
                                    setDemoError(null);
                                    setIsDemoMode(true);
                                }}
                                activeOpacity={0.75}
                                accessibilityLabel="Try Demo"
                                accessibilityRole="button"
                                accessibilityHint="Explore Free Learning and Classroom Mode in demo mode"
                            >
                                <Sparkles size={24} color="#722ED1" strokeWidth={2.5} />
                                <Text style={styles.demoButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Try Demo</Text>
                            </TouchableOpacity>
                        </>
                    )}
                </View>

                <CopyrightFooter />
            </ScrollView>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#E6F7FF',
    },
    scrollView: {
        flex: 1,
        width: '100%',
    },
    scrollContent: {
        flexGrow: 1,
        justifyContent: 'center',
    },
    blob: {
        position: 'absolute',
        borderRadius: 999,
    },
    blobTopRight: {
        width: 180,
        height: 180,
        backgroundColor: '#BAE7FF',
        top: 20,
        right: -50,
        opacity: 0.6,
    },
    blobMidLeft: {
        width: 140,
        height: 140,
        backgroundColor: '#FFD8BF',
        top: 260,
        left: -40,
        opacity: 0.5,
    },
    blobBottomRight: {
        width: 220,
        height: 220,
        backgroundColor: '#D9F7BE',
        bottom: 40,
        right: -70,
        opacity: 0.55,
    },
    blobTopLeft: {
        width: 100,
        height: 100,
        backgroundColor: '#FFCCC7',
        top: 100,
        left: -20,
        opacity: 0.45,
    },
    sparkle: {
        position: 'absolute',
        opacity: 0.8,
    },
    dot: {
        position: 'absolute',
        width: 10,
        height: 10,
        borderRadius: 5,
        opacity: 0.5,
    },
    dot1: {
        backgroundColor: '#52C41A',
        top: 180,
        left: 80,
    },
    dot2: {
        backgroundColor: '#FAAD14',
        top: 240,
        right: 100,
        width: 14,
        height: 14,
        borderRadius: 7,
    },
    dot3: {
        backgroundColor: '#1890FF',
        bottom: 280,
        right: 40,
    },
    dot4: {
        backgroundColor: '#EB2F96',
        bottom: 160,
        left: 100,
        width: 8,
        height: 8,
        borderRadius: 4,
    },
    content: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 24,
    },
    iconCluster: {
        width: 180,
        height: 140,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 32,
    },
    mainOrb: {
        width: 120,
        height: 120,
        borderRadius: 60,
        backgroundColor: '#FFF7E6',
        justifyContent: 'center',
        alignItems: 'center',
        shadowColor: '#FAAD14',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.25,
        shadowRadius: 12,
        elevation: 6,
        zIndex: 2,
    },
    iconOrb: {
        width: 56,
        height: 56,
        borderRadius: 28,
        justifyContent: 'center',
        alignItems: 'center',
        position: 'absolute',
        elevation: 4,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
    },
    orbBackLeft: {
        backgroundColor: '#52C41A',
        left: 0,
        bottom: 10,
        zIndex: 1,
    },
    orbBackRight: {
        backgroundColor: '#1890FF',
        right: 0,
        bottom: 10,
        zIndex: 1,
    },
    title: {
        fontSize: 36,
        fontWeight: '900',
        color: '#1E293B',
        marginBottom: 10,
        textAlign: 'center',
    },
    subtitle: {
        fontSize: 18,
        color: '#475569',
        marginBottom: 48,
        textAlign: 'center',
        lineHeight: 26,
        fontWeight: '600',
    },
    scanButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#52C41A',
        paddingHorizontal: 40,
        paddingVertical: 22,
        borderRadius: 20,
        minHeight: 76,
        minWidth: 280,
        gap: 12,
        shadowColor: '#52C41A',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.35,
        shadowRadius: 12,
        elevation: 8,
    },
    scanButtonText: {
        color: '#FFFFFF',
        fontSize: 24,
        fontWeight: '900',
    },
    demoButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F9F0FF',
        paddingHorizontal: 32,
        paddingVertical: 18,
        borderRadius: 16,
        minHeight: 64,
        minWidth: 240,
        gap: 10,
        marginTop: 16,
        borderWidth: 2,
        borderColor: '#D3ADF7',
    },
    demoButtonBusy: {
        opacity: 0.85,
        borderColor: '#B37FEB',
    },
    demoButtonText: {
        color: '#722ED1',
        fontSize: 22,
        fontWeight: '800',
    },
    demoErrorText: {
        color: '#FA8C16',
        fontSize: 18,
        fontWeight: '700',
        marginTop: 12,
        textAlign: 'center',
    },
    schoolBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F0FDFA',
        borderColor: '#99F6E4',
        borderWidth: 1.5,
        borderRadius: 20,
        paddingHorizontal: 14,
        paddingVertical: 6,
        gap: 6,
        marginBottom: 12,
    },
    schoolBadgeText: {
        fontSize: 14,
        fontWeight: '800',
        color: '#0F766E',
    },
    modeButtonsContainer: {
        width: '100%',
        maxWidth: 380,
        gap: 14,
        marginBottom: 8,
    },
    freestyleModeCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFBEB',
        borderColor: '#F59E0B',
        borderWidth: 2.5,
        borderBottomWidth: 5,
        borderRadius: 22,
        paddingVertical: 16,
        paddingHorizontal: 18,
        gap: 14,
        shadowColor: '#F59E0B',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
        elevation: 5,
    },
    classroomModeCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F0FDF4',
        borderColor: '#22C55E',
        borderWidth: 2.5,
        borderBottomWidth: 5,
        borderRadius: 22,
        paddingVertical: 16,
        paddingHorizontal: 18,
        gap: 14,
        shadowColor: '#22C55E',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
        elevation: 5,
    },
    classroomModeCardBusy: {
        opacity: 0.85,
    },
    demoBadgeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        marginBottom: 12,
    },
    backButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F1F5F9',
        borderColor: '#CBD5E1',
        borderWidth: 1,
        borderRadius: 20,
        paddingHorizontal: 12,
        paddingVertical: 6,
        gap: 4,
    },
    backButtonText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#475569',
    },
    demoHubBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F9F0FF',
        borderColor: '#D3ADF7',
        borderWidth: 1.5,
        borderRadius: 20,
        paddingHorizontal: 14,
        paddingVertical: 6,
        gap: 6,
    },
    demoHubBadgeText: {
        fontSize: 14,
        fontWeight: '800',
        color: '#722ED1',
    },
    modeIconCircleGold: {
        width: 52,
        height: 52,
        borderRadius: 26,
        backgroundColor: '#FEF3C7',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modeIconCircleGreen: {
        width: 52,
        height: 52,
        borderRadius: 26,
        backgroundColor: '#DCFCE7',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modeTextContainer: {
        flex: 1,
    },
    modeTitle: {
        fontSize: 20,
        fontWeight: '900',
        color: '#1E293B',
    },
    modeDescription: {
        fontSize: 13,
        fontWeight: '700',
        color: '#64748B',
        marginTop: 2,
    },
    freestyleLockedCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F8FAFC',
        borderColor: '#CBD5E1',
        borderWidth: 1.5,
        borderRadius: 16,
        paddingHorizontal: 18,
        paddingVertical: 12,
        gap: 8,
        marginTop: 14,
        maxWidth: 340,
    },
    freestyleLockedText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#475569',
        flex: 1,
    },
});
